import { Badge } from '@/components/ui/badge'
import { Calendar, ClipboardList, Stethoscope } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SurveySectionHeaderProps {
  /** 1-based appointment number — shown when there are multiple appointments. */
  appointmentSectionNumber?: number
  /** 'general' or 'service' — controls the icon and label style. */
  sectionType?: 'general' | 'service'
  /** Translated title for the section (service name, or "General Questions"). */
  sectionTitleTranslations?: { en: string; ar: string }
  /** ISO date string for the appointment's scheduled time. */
  appointmentScheduledAt?: string
  /** Whether the current locale is Arabic. */
  isArabic: boolean
}

/**
 * Renders a compact section header inside a question Card.
 *
 * - **General**: shows a clipboard icon + "General Questions"
 * - **Appointment**: shows "Appointment N" with optional date badge
 * - **Service**: shows a stethoscope icon + service name + optional date/time
 *
 * If only one appointment exists, `appointmentSectionNumber` is not set and
 * the appointment-level header is not rendered.
 */
export function SurveySectionHeader({
  appointmentSectionNumber,
  sectionType,
  sectionTitleTranslations,
  appointmentScheduledAt,
  isArabic,
}: SurveySectionHeaderProps) {
  // Format a date string for display
  const formattedDate = appointmentScheduledAt
    ? formatScheduledDate(appointmentScheduledAt, isArabic)
    : null

  return (
    <div className="mb-5 space-y-2">
      {/* Appointment-level header (only for multi-appointment orders) */}
      {appointmentSectionNumber != null && (
        <div className="flex items-center gap-2 mb-1">
          <div
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full',
              'bg-primary/10 text-primary',
            )}
          >
            <Calendar className="h-3.5 w-3.5" />
          </div>
          <span className="text-sm font-semibold text-primary">
            {isArabic
              ? `الموعد ${appointmentSectionNumber}`
              : `Appointment ${appointmentSectionNumber}`}
          </span>
          {formattedDate && (
            <Badge variant="outline" className="text-xs font-normal">
              {formattedDate}
            </Badge>
          )}
        </div>
      )}

      {/* Section-level header (General / Service) */}
      {sectionType != null && sectionTitleTranslations && (
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2',
            sectionType === 'general'
              ? 'bg-muted/60'
              : 'bg-primary/[0.04] border border-primary/10',
          )}
        >
          {sectionType === 'general' ? (
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Stethoscope className="h-4 w-4 text-primary/70" />
          )}
          <span
            className={cn(
              'text-sm font-medium',
              sectionType === 'general'
                ? 'text-muted-foreground'
                : 'text-foreground',
            )}
          >
            {isArabic
              ? sectionTitleTranslations.ar
              : sectionTitleTranslations.en}
          </span>

          {/* Show date badge on service header when no appointment header is present */}
          {sectionType === 'service' &&
            appointmentSectionNumber == null &&
            formattedDate && (
              <Badge variant="outline" className="ml-auto text-xs font-normal">
                {formattedDate}
              </Badge>
            )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatScheduledDate(iso: string, isArabic: boolean): string {
  try {
    const date = new Date(iso)
    if (isNaN(date.getTime())) return iso

    return date.toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}
